import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import CustomAlert from '../CustomAlert';
import { t } from '../../i18n';

const apiUrl = process.env.REACT_APP_API_URL;

const EMPTY = { name: '', address: '', email: '', phone: '', website: '' };

/**
 * Manufacturer edit form. Same shape as ManufacturerAdd: no page chrome, so it
 * sits either in the Drawer on the list page or standalone on
 * /manufacturer/edit/:id.
 *
 * id — from props in the drawer, from the route param standalone
 * onSuccess(updated) — drawer closes and the list refreshes
 */
export default function ManufacturerEdit({ id: idProp, onSuccess }) {
  const params = useParams();
  const id = idProp ?? params.id;
  const navigate = useNavigate();

  const [manufacturer, setManufacturer] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState({ isOpen: false, type: 'error', title: '', message: '' });

  const closeAlert = () => setAlert((a) => ({ ...a, isOpen: false }));

  useEffect(() => {
    const token = localStorage.getItem('token');
    fetch(`${apiUrl}/manufacturer/view/${id}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
      .then((res) => {
        if (!res.ok) throw new Error(t('Производителот не може да се вчита'));
        return res.json();
      })
      // NULL columns would flip the inputs to uncontrolled — keep '' for those.
      .then((data) =>
        setManufacturer({
          ...EMPTY,
          ...Object.fromEntries(Object.entries(data).filter(([, v]) => v !== null)),
        })
      )
      .catch((err) => {
        console.error('Failed to load manufacturer:', err);
        setAlert({
          isOpen: true,
          type: 'error',
          title: t('Грешка'),
          message: t('Неуспешно вчитување на производителот.'),
        });
      });
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setManufacturer((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!manufacturer.name.trim()) return;

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/manufacturer/edit/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ ...manufacturer, name: manufacturer.name.trim() }),
      });

      // The old version ignored the response entirely and always navigated away,
      // so a failed save looked identical to a successful one.
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || t('Производителот не може да се ажурира'));

      if (onSuccess) {
        onSuccess(data);
      } else {
        navigate('/manufacturer/list');
      }
    } catch (error) {
      setAlert({ isOpen: true, type: 'error', title: t('Грешка'), message: error.message });
    } finally {
      setSaving(false);
    }
  };

  const field = 'border border-gray-300 p-3 rounded w-full';

  return (
    <>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('Назив *')}</label>
          <input
            type="text"
            name="name"
            value={manufacturer.name}
            onChange={handleChange}
            className={field}
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('Адреса')}</label>
          <input type="text" name="address" value={manufacturer.address} onChange={handleChange} className={field} />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('Е-пошта')}</label>
          <input type="email" name="email" value={manufacturer.email} onChange={handleChange} className={field} />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('Телефон')}</label>
          <input type="text" name="phone" value={manufacturer.phone} onChange={handleChange} className={field} />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('Веб-страна')}</label>
          <input type="text" name="website" value={manufacturer.website} onChange={handleChange} className={field} />
        </div>

        <div className="pt-4 border-t border-gray-200">
          <button
            type="submit"
            disabled={saving}
            className="w-full px-4 py-2.5 bg-green-500 hover:bg-green-600 disabled:opacity-60 disabled:cursor-not-allowed text-white rounded-lg font-medium text-sm transition-colors"
          >
            {saving ? t('Се зачувува...') : t('Зачувај промени')}
          </button>
        </div>
      </form>

      <CustomAlert
        isOpen={alert.isOpen}
        onClose={closeAlert}
        type={alert.type}
        title={alert.title}
        message={alert.message}
      />
    </>
  );
}
